import { IconButton, Menu, MenuButton, MenuList } from "@chakra-ui/react";
import { RiMore2Fill } from "react-icons/ri";
import { useFormTemplateSettings } from "src/hooks/useFormTemplateSettings";
import { useTimezoneName } from "src/hooks/useOrganization";
import { isOfferExpired } from "src/services/form/offer";
import * as GQL from "src/types/graphql";
import { AcceptOffer } from "../../ActionItem/AcceptOffer";
import { DeclineOffer } from "../../ActionItem/DeclineOffer";
import { WithdrawForm } from "../../ActionItem/WithdrawForm";
import {
  canAcceptApplicationOffer,
  canDeclineApplicationOffer,
  canWithdrawApplication,
} from "../helpers";

export type Offer = {
  readonly id: uuid;
  readonly status: GQL.offer_status_enum;
  readonly expires_at?: string | null;
};
export type Waitlist = {
  readonly status: GQL.waitlist_status_enum;
};
export type School = {
  readonly id: uuid;
  readonly name: string;
  readonly reference_id: string | null;
};
interface OptionsMenuProps {
  acceptedOfferSchoolName: string | undefined;
  formId: string;
  formKey: string;
  formTemplateId: uuid;
  offer: Offer | null;
  waitlist: Waitlist | null;
  gradeLabel: string;
  school: School;
  studentName: string;
  enrollmentPeriodKey: string | null;
  isActionPending?: boolean;
  onActionPendingChange?: (pending: boolean) => void;
}

export function OptionsMenu(props: OptionsMenuProps) {
  const {
    acceptedOfferSchoolName,
    formId,
    formKey,
    formTemplateId,
    offer,
    waitlist,
    gradeLabel,
    school,
    studentName,
    enrollmentPeriodKey,
    isActionPending = false,
    onActionPendingChange,
  } = props;

  const timezoneName = useTimezoneName();
  const formTemplateSettings = useFormTemplateSettings(formTemplateId);
  let acceptAction;
  let declineAction;
  let withdrawAction;

  if (offer) {
    const offerHasExpired = isOfferExpired({
      expires_at: offer.expires_at,
      timezone_name: timezoneName,
    });

    if (canAcceptApplicationOffer(offer)) {
      acceptAction = (
        <AcceptOffer
          formId={formId}
          formKey={formKey}
          formTemplateId={formTemplateId}
          acceptedOfferSchoolName={acceptedOfferSchoolName}
          offerId={offer.id}
          school={school.name}
          schoolReferenceId={school.reference_id}
          studentName={studentName}
          enrollmentPeriodKey={enrollmentPeriodKey}
          isDisabled={offerHasExpired || isActionPending}
          onActionPendingChange={onActionPendingChange}
        />
      );
    }

    if (
      formTemplateSettings.hasData() &&
      canDeclineApplicationOffer(
        offer,
        formTemplateSettings.data.allowDeclineAcceptedOffer
      )
    ) {
      declineAction = (
        <DeclineOffer
          formId={formId}
          offerId={offer.id}
          school={school.name}
          isDisabled={offerHasExpired || isActionPending}
          onActionPendingChange={onActionPendingChange}
        />
      );
    }
  } else if (canWithdrawApplication(waitlist)) {
    withdrawAction = (
      <WithdrawForm
        formId={formId}
        grade={gradeLabel}
        school={school.name}
        schoolId={school.id}
        isDisabled={isActionPending}
        onActionPendingChange={onActionPendingChange}
      />
    );
  }

  if (!acceptAction && !declineAction && !withdrawAction) {
    return null;
  }

  return (
    <Menu placement="bottom-end">
      <MenuButton
        as={IconButton}
        icon={<RiMore2Fill />}
        marginInlineStart="auto"
        onClick={() => {}}
        size="sm"
        variant="ghost"
        isDisabled={isActionPending}
      />

      <MenuList>
        {acceptAction}
        {declineAction}
        {withdrawAction}
      </MenuList>
    </Menu>
  );
}
